import { Controller } from "@hotwired/stimulus"

/**
 * AI JSON Inspector Controller — CONEK-3365 Wave 4.
 *
 * Renders raw cable payloads in the right rail of the admin AI playground.
 * The ai--companion controller in the center pane forwards every broadcast
 * frame to #append(payload). PII is redacted DISPLAY-side here (NOT at the
 * cable wire — CompanionAgent#broadcast is byte-frozen) so the React and
 * Hotwire companion sidebars continue to receive un-redacted payloads with
 * legitimate contact email/phone values intact.
 *
 * REDACTED_KEYS mirrors packs/ai/app/models/mcp_request_log.rb:39 exactly.
 * The Vitest spec asserts size invariant (`expect(REDACTED_KEYS.size).toBe(17)`)
 * to fail CI if the Ruby side drifts.
 *
 * shiki is loaded via dynamic `await import("shiki/bundle/web")` inside
 * connect() so it lands in a separate Vite chunk — the admin shell bundle
 * stays small until the playground page actually mounts.
 */

// Mirror of packs/ai/app/models/mcp_request_log.rb:39 REDACTED_KEYS. Keep
// in sync — Vitest size invariant guards drift.
export const REDACTED_KEYS = new Set([
  "email",
  "emails",
  "phone",
  "phones",
  "mobile",
  "api_key",
  "api_token",
  "token",
  "password",
  "secret",
  "authorization",
  "auth",
  "bearer",
  "ssn",
  "tax_id",
  "vat",
  "credit_card",
])

export const REDACTED_PLACEHOLDER = "[REDACTED]"

/**
 * Recursively redact values under PII key names. Walks plain objects and
 * arrays; primitives are returned as-is. Pure function (input not mutated).
 */
export function redactDeep(value) {
  if (Array.isArray(value)) {
    return value.map((v) => redactDeep(v))
  }
  if (value && typeof value === "object") {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      if (REDACTED_KEYS.has(k.toLowerCase())) {
        out[k] = REDACTED_PLACEHOLDER
      } else {
        out[k] = redactDeep(v)
      }
    }
    return out
  }
  return value
}

export default class extends Controller {
  static targets = ["container", "emptyState"]
  static values = {
    themeLight: { type: String, default: "github-light" },
    themeDark: { type: String, default: "github-dark" },
  }

  async connect() {
    this.entries = []
    // Dynamic import — keeps shiki out of the admin shell synchronous bundle.
    // First playground mount pays the chunk-load cost; subsequent mounts hit
    // the browser HTTP cache.
    try {
      const mod = await import("shiki/bundle/web")
      this.codeToHtml = mod.codeToHtml
    } catch (err) {
      console.error("[ai--json-inspector] failed to load shiki:", err)
      // Inspector still works without syntax highlight — append() falls back
      // to plain <pre> rendering when codeToHtml is unset.
    }
  }

  async append(payload) {
    if (!this.hasContainerTarget) return

    const redacted = redactDeep(payload)
    const json = JSON.stringify(redacted, null, 2)

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }

    // Dual-theme: Shiki emits the light theme as inline `color`/`background-color`
    // plus `--shiki-dark`/`--shiki-dark-bg` CSS vars. The `.dark .shiki` rule in
    // application.css swaps to the dark vars when the app is in dark mode.
    const html = this.codeToHtml
      ? await this.codeToHtml(json, {
          lang: "json",
          themes: { light: this.themeLightValue, dark: this.themeDarkValue },
        })
      : `<pre class="overflow-x-auto rounded bg-gray-50 p-2 text-xs dark:bg-gray-800 dark:text-gray-100">${escapeHtml(json)}</pre>`

    const wrapper = document.createElement("details")
    wrapper.className = "mb-2 rounded border border-input"
    wrapper.open = true
    const type = redacted && typeof redacted === "object" ? redacted.type : null
    const summary = document.createElement("summary")
    summary.className =
      "flex cursor-pointer items-center justify-between px-2 py-1 text-xs text-muted-foreground"
    summary.innerHTML = `
      <span>${new Date().toISOString().slice(11, 23)} · ${escapeHtml(type ?? "unknown")}</span>
      <button type="button" class="text-xs underline" data-action="click->ai--json-inspector#copy">
        copy
      </button>
    `
    wrapper.appendChild(summary)
    const body = document.createElement("div")
    body.className = "border-t border-input px-2 py-1"
    body.innerHTML = html
    wrapper.appendChild(body)

    this.containerTarget.appendChild(wrapper)
    this.entries.push(redacted)
  }

  copy(event) {
    event.preventDefault()
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("details")
    if (!wrapper) return
    const pre = wrapper.querySelector("pre")
    if (!pre) return
    navigator.clipboard?.writeText(pre.textContent || "")
  }

  clear() {
    if (!this.hasContainerTarget) return
    this.containerTarget.innerHTML = ""
    this.entries = []
  }
}

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  )
}
